import { PLATFORM_FEE_RATE, vendorEarningsFromTotal } from '../constants/stripe';
import { supabase } from '../firebase/config';

async function getAuthHeaders(): Promise<HeadersInit> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) {
    throw new Error('You must be signed in');
  }
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

interface CreateCustomerInput {
  email: string;
  name: string;
  metadata?: Record<string, unknown>;
}

export interface VendorPaymentSplit {
  vendorId: string;
  totalCents: number;
  transferCents: number;
}

interface CreatePaymentIntentInput {
  amount: number;
  currency: string;
  customerId: string;
  metadata?: Record<string, unknown>;
  vendorSplits: VendorPaymentSplit[];
}

interface PaymentIntentResult {
  clientSecret: string;
  paymentIntentId: string;
  transferGroup?: string;
}

interface CheckoutLineItem {
  name: string;
  unitAmount: number;
  quantity: number;
}

interface CreateCheckoutSessionInput {
  orderId: string;
  amount: number;
  currency: string;
  customerEmail: string;
  customerName: string;
  vendorSplits: VendorPaymentSplit[];
  metadata?: Record<string, unknown>;
  lineItems: CheckoutLineItem[];
}

interface ConnectStatus {
  connected: boolean;
  onboardingComplete: boolean;
  payoutsEnabled: boolean;
  chargesEnabled: boolean;
  accountId?: string;
}

export function buildVendorSplitsFromCartGroups(
  vendorGroups: Record<string, { vendorId: string; total: number }>
): VendorPaymentSplit[] {
  return Object.values(vendorGroups).map((group) => {
    const totalCents = Math.round(group.total * 100);
    const transferCents = Math.round(vendorEarningsFromTotal(group.total) * 100);
    return {
      vendorId: group.vendorId,
      totalCents,
      transferCents,
    };
  });
}

class StripeService {
  private static instance: StripeService;
  private initialized = false;
  private publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY?.trim() || '';

  public static getInstance(): StripeService {
    if (!StripeService.instance) {
      StripeService.instance = new StripeService();
    }
    return StripeService.instance;
  }

  public getPublishableKey(): string {
    return this.publishableKey;
  }

  public isConfigured(): boolean {
    return Boolean(this.publishableKey);
  }

  public async initialize(): Promise<void> {
    if (!this.publishableKey) {
      console.warn(
        '[Stripe] REACT_APP_STRIPE_PUBLISHABLE_KEY is not set. Add it to .env.local and redeploy.'
      );
    }
    this.initialized = true;
  }

  public async createCustomer(input: CreateCustomerInput): Promise<string> {
    if (!this.initialized) {
      await this.initialize();
    }

    try {
      const response = await fetch('/api/create-customer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      });

      if (response.ok) {
        const payload = (await response.json()) as { customerId?: string };
        if (payload.customerId) return payload.customerId;
      }
    } catch (error) {
      console.warn('Falling back to local customer ID:', error);
    }

    return String(input.metadata?.userId || input.email || `customer_${Date.now()}`);
  }

  public async createPaymentIntent(input: CreatePaymentIntentInput): Promise<PaymentIntentResult> {
    if (!this.initialized) {
      await this.initialize();
    }

    const response = await fetch('/api/create-payment-intent', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });

    const payload = (await response.json()) as PaymentIntentResult & { error?: string };
    if (!response.ok || !payload.clientSecret || !payload.paymentIntentId) {
      throw new Error(payload.error || 'Failed to create payment intent');
    }

    return payload;
  }

  public async confirmCheckoutSession(sessionId: string): Promise<{ success: boolean; orderId?: string }> {
    const headers = await getAuthHeaders();
    const response = await fetch('/api/confirm-checkout', {
      method: 'POST',
      headers,
      body: JSON.stringify({ sessionId }),
    });

    const payload = (await response.json()) as {
      success?: boolean;
      orderId?: string;
      error?: string;
    };

    if (!response.ok || !payload.success) {
      throw new Error(payload.error || 'Failed to confirm checkout');
    }

    return { success: true, orderId: payload.orderId };
  }

  public async createCheckoutSession(
    input: CreateCheckoutSessionInput
  ): Promise<{ url: string; sessionId: string }> {
    const response = await fetch('/api/create-checkout-session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });

    const payload = (await response.json()) as { url?: string; sessionId?: string; error?: string };
    if (!response.ok || !payload.url) {
      throw new Error(payload.error || 'Failed to start Stripe Checkout');
    }

    return { url: payload.url, sessionId: payload.sessionId || '' };
  }

  public async startVendorOnboarding(input: {
    vendorId: string;
    email: string;
    country: string;
  }): Promise<void> {
    const response = await fetch('/api/stripe-connect?action=onboarding', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    const payload = (await response.json()) as { url?: string; error?: string };
    if (!response.ok || !payload.url) {
      throw new Error(payload.error || 'Failed to create Stripe onboarding link');
    }
    window.location.href = payload.url;
  }

  public async openVendorDashboard(vendorId: string): Promise<void> {
    const response = await fetch('/api/stripe-connect?action=dashboard', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ vendorId }),
    });
    const payload = (await response.json()) as { url?: string; error?: string };
    if (!response.ok || !payload.url) {
      throw new Error(payload.error || 'Failed to open Stripe dashboard');
    }
    window.location.href = payload.url;
  }

  public async resetVendorConnect(): Promise<void> {
    const headers = await getAuthHeaders();
    const response = await fetch('/api/stripe-connect?action=reset', {
      method: 'POST',
      headers,
    });
    const payload = (await response.json()) as { error?: string; reset?: boolean };
    if (!response.ok || !payload.reset) {
      throw new Error(payload.error || 'Failed to reset Stripe connection');
    }
  }

  public async getVendorConnectStatus(vendorId: string): Promise<ConnectStatus> {
    const disconnected: ConnectStatus = {
      connected: false,
      onboardingComplete: false,
      payoutsEnabled: false,
      chargesEnabled: false,
    };

    try {
      const response = await fetch(
        `/api/stripe-connect?action=status&vendorId=${encodeURIComponent(vendorId)}`
      );
      const payload = (await response.json()) as ConnectStatus & { error?: string };
      if (!response.ok || payload.error) {
        console.warn('[Stripe] Connect status unavailable:', payload.error || response.status);
        return disconnected;
      }
      return payload;
    } catch (err) {
      console.warn('[Stripe] Connect status fetch failed:', err);
      return disconnected;
    }
  }
}

export { PLATFORM_FEE_RATE };
export default StripeService;
